/**
 * +autoattack: auto-attack HUD.
 *  - A crossed-swords badge on the target frame's portrait while the hero is auto-attacking that
 *    target (WoW's attack highlight).
 *  - An optional swing timer (Gameplay settings, default off) above the player frame: main hand
 *    (or Auto Shot for a bow), plus the off hand for a dual wielder. A bar fills toward the next
 *    swing; it glows when the swing is due but waiting (out of range / not facing).
 * `swingProgress` is the pure part (unit-tested).
 */
import type { DeepReadonly, SwingState, Unit, UnitId, World } from '@keystone/sim';
import { el } from './dom';
import { gameplaySettings } from './gameplaySettings';

/** 0..1 toward the next swing at `nowMs` (1 = due). */
export function swingProgress(speedMs: number, nextAt: number, haste: number, nowMs: number): number {
  const interval = Math.max(1, speedMs / (1 + haste));
  const left = nextAt - nowMs;
  return left <= 0 ? 1 : Math.max(0, Math.min(1, 1 - left / interval));
}

const SWORDS_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><g stroke="currentColor" stroke-width="2.2" stroke-linecap="round" fill="none">' +
  '<path d="M4.5 4.5L16.5 16.5"/><path d="M19.5 4.5L7.5 16.5"/><path d="M13.5 18.5L18.5 13.5"/><path d="M5.5 13.5L10.5 18.5"/>' +
  '<path d="M17.5 17.5L20 20"/><path d="M6.5 17.5L4 20"/></g></svg>';

interface Bar {
  root: HTMLElement;
  fill: HTMLElement;
  shown: number;
}

export class AutoAttackHud {
  private readonly badge: HTMLElement | null;
  private readonly timer: HTMLElement;
  private readonly bars: [Bar, Bar];
  private badgeOn = false;
  private timerOn = false;
  private ohOn = false;

  constructor(timerParent: HTMLElement, before: HTMLElement | null, targetFrameRoot: HTMLElement | null) {
    const portrait = targetFrameRoot?.querySelector<HTMLElement>('.ft-portrait') ?? null;
    this.badge = portrait ? el('div', 'aa-badge hidden', portrait) : null;
    if (this.badge) {
      this.badge.innerHTML = SWORDS_SVG;
      this.badge.title = 'Auto-attacking';
    }
    this.timer = el('div', 'aa-timer hidden');
    timerParent.insertBefore(this.timer, before);
    const bar = (label: string): Bar => {
      const root = el('div', 'aa-bar', this.timer);
      const fill = el('div', 'aa-fill', root);
      el('span', 'aa-label', root, label);
      return { root, fill, shown: -1 };
    };
    this.bars = [bar('Main hand'), bar('Off hand')];
    this.bars[1].root.classList.add('hidden');
  }

  update(world: DeepReadonly<World>, playerId: UnitId | null, selectedId: UnitId | null, nowMs: number): void {
    const me = playerId ? (world.units[playerId] as DeepReadonly<Unit> | undefined) : undefined;
    const attacking = !!me?.alive && !!me.autoAttack;
    const badge = attacking && !!selectedId && me!.targetId === selectedId;
    if (this.badge && badge !== this.badgeOn) {
      this.badgeOn = badge;
      this.badge.classList.toggle('hidden', !badge);
    }
    const sw = me?.swing as DeepReadonly<SwingState> | null | undefined;
    const timer = gameplaySettings.get().swingTimer && !!sw && !!me?.alive;
    if (timer !== this.timerOn) {
      this.timerOn = timer;
      this.timer.classList.toggle('hidden', !timer);
    }
    if (!timer || !sw) return;
    this.timer.classList.toggle('idle', !attacking);
    const haste = sw.haste ?? 0;
    const label = sw.kind === 'ranged' ? 'Auto Shot' : sw.oh ? 'Main hand' : 'Swing';
    this.paint(this.bars[0], swingProgress(sw.speedMs, sw.nextAt, haste, nowMs), label, attacking && !!sw.blocked);
    const oh = !!sw.oh;
    if (oh !== this.ohOn) {
      this.ohOn = oh;
      this.bars[1].root.classList.toggle('hidden', !oh);
    }
    if (sw.oh) this.paint(this.bars[1], swingProgress(sw.oh.speedMs, sw.oh.nextAt, haste, nowMs), 'Off hand', attacking && !!sw.blocked);
  }

  private paint(b: Bar, p: number, label: string, waiting: boolean): void {
    const q = Math.round(p * 200) / 200;
    if (q !== b.shown) {
      b.shown = q;
      b.fill.style.transform = `scaleX(${q})`;
    }
    const lab = b.root.lastElementChild as HTMLElement;
    if (lab.textContent !== label) lab.textContent = label;
    b.root.classList.toggle('waiting', waiting && p >= 1);
  }
}
